<template>
  <div id="app">
    <loading :active="loading" class="loader" color="#131D65"></loading>
    <v-app>
      <v-main v-if="!loading" :style="isHomeOrFinishPage ? { 'background-color': getAssets.primaryButtonBackground } : ''">
        <router-view />
        <cookies-banner v-if="showCookiesBanner" @close="closeCookies" />
        <google-analytics @init-google-analytics="initGoogleAnalytics()" />
      </v-main>
      <social-networks-footer v-if="!loading && !isApp" @open-cookies-banner="openCookies" @close="closeCookies" />
    </v-app>
  </div>
</template>

<script>
import { validCpf, validCnpj, telefoneValido, idadeMinima, idadeMaxima, dataValida, cidadeValida } from '@/config/custom-validators.js'
import Loading from 'vue-loading-overlay'
import { mapActions } from 'vuex'

export default {
  components: {
    Loading,
    CookiesBanner: () => import('@/components/layout/CookiesBanner.vue'),
    SocialNetworksFooter: () => import('@/components/layout/SocialNetworksFooter.vue'),
    GoogleAnalytics: () => import('@/plugins/google-analytics/GoggleAnalytics.vue')
  },
  data: () => ({
    cookieBanner: false,
    pixelInit: false,
    googleAnalyticsInit: false,
    localLoading: false
  }),
  computed: {
    isApp() {
      return this.$store.getters.IS_APP
    },
    hostname() {
      return this.$store.getters.GET_DOMAIN
    },
    isHomeOrFinishPage({ commmit, state }) {
      return ['home', 'finalizar', '404'].includes(this.$route.name)
    },
    getAssets() {
      return this.$store.getters.GET_ASSETS
    },
    loading() {
      return this.$store.getters.LOADING || this.localLoading
    },
    showCookiesBanner() {
      return (!!this.pixelCode || !!this.googleAnalyticsCode) && this.cookieBanner
    },
    pixelCode() {
      return this.$store.getters.PIXEL
    },
    googleAnalyticsCode() {
      return this.$store.getters.GOOGLE_ANALYTICS
    },
    isSmsRecover() {
      return this.$store.getters.IS_RECOVER_SMS
    },
    localStorageAccepted() {
      return window.localStorage.getItem('pixelCookiePermission') === 'true'
    }
  },
  watch: {
    $route(to, from) {
      window.scrollTo(0, 0)
      if (!from.name || to.name !== 'nova-senha' || this.isSmsRecover) return
      this.$router.push({ path: '/home' })
    }
  },
  async created() {
    await this.loadHotsiteConfig()

    // Use reliable pathname throughout
    const pathname = window.location.pathname
    const urlParams = new URLSearchParams(window.location.search)

    if (this.$store.getters.ALLOWED_MODULES.captcha) {
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.$store.getters.CAPTCHA_KEY}`
      script.async = true
      document.head.appendChild(script)
    }
    if (pathname === '/nova-senha') {
      if (!this.isSmsRecover) {
        this.localLoading = true
        await this.$store
          .dispatch('tokenValidation', { token: urlParams.get('token') })
          .then(() => {
            // keep user in this page
          })
          .catch(err => {
            console.error(err)
            this.$snackbar({
              message: 'Esse e-mail de alteração de senha já foi utilizado ou não é mais válido.',
              snackbarColor: 'error'
            })
            this.$router.push({ path: '/home' })
          })
        this.localLoading = false
        return
      }
    }
    if (pathname === '/recuperar-senha' && urlParams.get('external_auth') === 'true' && urlParams.get('document_no') !== null) {
      this.localLoading = true
      await this.$store
        .dispatch('checkUniqueUser', { username: urlParams.get('document_no') })
        .then(() => {
          // keep user in this page
          this.$store.commit('SET_IS_EXTERNAL_AUTH', true)
        })
        .catch(err => {
          console.error(err)
          this.$snackbar({
            message: 'Não foi possível iniciar a recuperação de senha.',
            snackbarColor: 'error'
          })
          this.$router.push({ path: '/home' })
        })
      this.localLoading = false
      return
    }
    if (!this.localStorageAccepted) this.cookieBanner = true
    else {
      this.initFacebookPixel()
    }

    if (['/contato', '/confirm', '/finalizar', '/whatsapp'].includes(pathname)) {
      return
    }
    if (urlParams.get('is_app') !== 'true') {
      this.$router.push('home').catch(() => {})
    }
    if (urlParams.get('access_token')) {
      const token = urlParams.get('access_token')
      localStorage.setItem('token', token)
      this.$store.commit('SET_IS_APP', true)
      this.$store.commit('ACCESS_TOKEN', token)
      this.tokenLogin(token)
      this.$router.replace({
        ...this.$router.currentRoute,
        query: {
          token: undefined
        }
      })
    }
  },
  async mounted() {
    // Setup validators
    this.$validator.extend('validCpf', validCpf)
    this.$validator.extend('validCnpj', validCnpj)
    this.$validator.extend('idadeMinima', idadeMinima)
    this.$validator.extend('cidadeValida', cidadeValida)
    this.$validator.extend('idadeMaxima', idadeMaxima)
    this.$validator.extend('dataValida', dataValida)
    this.$validator.extend('telefoneValido', telefoneValido)
  },
  methods: {
    ...mapActions(['setHotsiteConfig']),
    async loadHotsiteConfig() {
      const configData = document.documentElement.getAttribute('data-hotsite-config')
      const config = JSON.parse(configData)
      if (config?.data?.hotsite?.accountId) {
        this.setHotsiteConfig(config.data)
        this.$store.commit('LOADING', false)
        return
      }
      try {
        await this.$store.dispatch('getClient', this.hostname)
      } catch (error) {
        console.error(error)
        if (this.$route.path !== '/404') this.$router.push('404')
      }
    },
    tokenLogin(token) {
      this.$store.commit('LOADING', true)
      this.$store
        .dispatch('tokenLogin', { token })
        .then()
        .catch(err => {
          console.error(err)
          this.$snackbar({
            message: 'Token inválido.',
            snackbarColor: 'error'
          })
        })
        .finally(() => {
          this.$store.commit('LOADING', false)
        })
    },
    initFacebookPixel() {
      const permission = window.localStorage.getItem('pixelCookiePermission') === 'true'
      if (!this.showCookiesBanner && permission && !this.pixelInit) {
        window.fbq('init', this.pixelCode)
        window.fbq('track', 'PageView')
        this.pixelInit = true
      }
    },
    gtag() {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(arguments)
    },
    initGoogleAnalytics() {
      const permission = window.localStorage.getItem('googleAnalyticsCookiePermission') === 'true'
      if (!this.showCookiesBanner && permission && !this.googleAnalyticsInit) {
        window.gtag('js', new Date())

        window.gtag('config', this.googleAnalyticsCode)
        this.googleAnalyticsInit = true
      }
    },
    closeCookies(accept) {
      this.cookieBanner = false
      if (accept) {
        this.initFacebookPixel()
        this.initGoogleAnalytics()
      }
    },
    openCookies() {
      this.cookieBanner = true
    }
  }
}
</script>

<style lang="scss">
.col-30 {
  width: 15%;
}
.col-50 {
  width: 50%;
}
.col-100 {
  width: 100%;
}
.button-height {
  height: 44px !important;
}
.flag {
  display: block;
  height: 30px;
}
.loader {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}
.register-page {
  overflow-x: auto;
  background-color: white;
  height: calc(100% + 50px);
  .title-block {
    margin-top: 24px;
    margin-left: 40px;
    margin-bottom: 16px;
    .page-title {
      font-family: 'Roboto', medium;
      font-weight: 500;
      font-size: 36px;
      opacity: 0.86;
      margin: 0;
      color: rgba(0, 0, 0, 0.87);
    }
  }
  .form-box {
    font-family: 'Roboto', sans-serif;
    margin-left: 40px;
    margin-top: 24px;

    .required-fields {
      color: #6f6f6f;
      font-size: 16px;
      span {
        color: #1864ab;
      }
    }
    .form {
      .label {
        display: inline-block;
        margin-bottom: 4px;
        font-size: 18px;
        color: #6f6f6f;
        span {
          color: #1864ab;
        }
        &.disabled {
          color: rgba(0, 0, 0, 0.3);
          span {
            color: rgba(0, 0, 0, 0.3);
          }
        }
      }
      .input {
        &.v-input--is-disabled {
          .v-input__slot {
            border: 1px solid rgba(0, 0, 0, 0.3) !important;
          }
        }
        .v-input__slot {
          border: 1px solid #6f6f6f;
          &:hover {
            border: 1px solid #228be6 !important;
          }
        }
        &.v-input--is-focused {
          .v-input__slot {
            background-color: #fff9f2 !important;
            outline: none;
            border: 1px solid #228be6;
            input {
              color: #1864ab;
            }
          }
        }
        &.error--text {
          .v-input__slot {
            border: 1.6px solid #f03e3e;
          }
        }
        &.input-cep {
          .v-input--is-disabled:not(.v-input--is-readonly) {
            pointer-events: all !important;
          }
          .v-input__append-outer .v-icon,
          .v-input__prepend-outer .v-icon {
            pointer-events: all !important;
            color: rgba(0, 0, 0, 0.54) !important;
          }
        }
      }
      .register-button {
        font-family: 'Roboto', medium;
        font-weight: 500;
        font-size: 23px;
        color: #fff;
        width: 220px;
        height: 66px;
        border-radius: 5px;
        background-color: #1864ab;
        border-color: #707070;
        margin: 32px 0 16px;
      }
    }
  }
}
</style>
